import { useQuery } from "@tanstack/react-query";
import {
  ArrowDownRight,
  ArrowLeftRight,
  ArrowUpRight,
  Building2,
  Minus,
  ReceiptText,
  ShoppingCart,
  TrendingUp,
} from "lucide-react";
import { useMemo, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import type { BatchOutPaymentRow, BatchOutReport } from "@tilt/pos-sdk";

import { MethodIconForType } from "../components/sale/MethodIconForType";
import { LocationSubsetPicker } from "../components/scope/LocationSubsetPicker";
import { Alert } from "../components/ui/Alert";
import { Card } from "../components/ui/Card";
import { Field, TextInput } from "../components/ui/Field";
import { PageSpinner } from "../components/ui/Spinner";
import { getPosClient } from "../lib/api";
import { formatCents } from "../lib/format";
import { usePageTitle } from "../lib/use-page-title";
import { cn } from "../lib/utils";

/**
 * V2 Dashboard - analytics-first surface that complements /reports.
 *
 * Reports is the reconciliation workflow (period batches, breakdowns,
 * detail rows, CSV export). Dashboard is the at-a-glance operational
 * view: how is the business trending right now versus the prior
 * period? Where is volume concentrated by method, department, device?
 *
 * Both pages back onto the same ``/pos/v1/reports/batch-out`` endpoint
 * - reuse over a separate analytics query keeps cache hits warm
 * (toggling between the two pages doesn't re-fetch) and means the
 * scope-narrowing + Location column logic lives in one place.
 *
 * Period comparison: we fetch the configured range AND a second query
 * over the same-length window immediately preceding it, then compute
 * KPI deltas client-side (% change vs. prior). The toggle defaults
 * on; users on a brand-new env (or short window) just see the deltas
 * render against zero, with the helper text "vs. prior period".
 */

const DEFAULT_LOOKBACK_DAYS = 30;
// High-value cutoff for the "Recent High-Value Transactions" tail.
// Drop the lowest 5 charges from view so the table reads as
// "interesting tonight" rather than "every cash handoff".
const HIGH_VALUE_TOP_N = 10;

export function DashboardPage() {
  const { t, i18n } = useTranslation();
  usePageTitle("dashboard");

  const [from, setFrom] = useState<string>(() => nDaysAgoUtcIso(DEFAULT_LOOKBACK_DAYS));
  const [to, setTo] = useState<string>(() => todayUtcIso());
  const [compare, setCompare] = useState<boolean>(true);

  const profile = useQuery({
    queryKey: ["profile"],
    queryFn: () => getPosClient().getProfile(),
  });

  const active = profile.data?.active_membership ?? null;
  const scopeKind = active?.scope_kind ?? "location";
  const showLocationColumn = scopeKind !== "location";

  // Org / partner / platform-scoped operators may want a consolidated
  // rollup OR a picked subset. Empty list == consolidated. Picker
  // self-hides for location-scoped operators because there's only
  // one location to pick anyway.
  const [locationIds, setLocationIds] = useState<ReadonlyArray<string>>([]);
  const showSubsetPicker = scopeKind !== "location";
  const sortedIds = useMemo(() => [...locationIds].sort().join(","), [locationIds]);

  const current = useQuery({
    queryKey: ["pos", "dashboard", "current", { from, to, scopeKind, locationIds: sortedIds }],
    queryFn: () =>
      getPosClient().getBatchOutReport({
        date_from: from,
        date_to: to,
        period: "daily",
        ...(locationIds.length > 0 ? { location_ids: locationIds } : {}),
      }),
    enabled: !!from && !!to && from <= to && !!active,
    staleTime: 30_000,
  });

  // Prior-period window of equal length, ending the day before ``from``.
  const prior = useMemo(() => deriveOffsetWindow(from, to), [from, to]);
  const previous = useQuery({
    queryKey: [
      "pos",
      "dashboard",
      "previous",
      {
        from: prior.from,
        to: prior.to,
        scopeKind,
        enabled: compare,
        locationIds: sortedIds,
      },
    ],
    queryFn: () =>
      getPosClient().getBatchOutReport({
        date_from: prior.from,
        date_to: prior.to,
        period: "daily",
        ...(locationIds.length > 0 ? { location_ids: locationIds } : {}),
      }),
    enabled: compare && !!from && !!to && from <= to && !!active,
    staleTime: 30_000,
  });

  if (profile.isPending) {
    return <PageSpinner label={t("common.loading")} />;
  }

  if (!active) {
    return (
      <div className="max-w-md py-12">
        <Alert tone="warn" title={t("dashboard.noScopeTitle")}>
          {t("dashboard.noScopeBody")}
        </Alert>
      </div>
    );
  }

  const data = current.data ?? null;
  const compareData = compare ? (previous.data ?? null) : null;
  const locale = i18n.language || "en";

  return (
    <div className="space-y-6 pb-12">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-muted">
            {t("dashboard.kicker")}
          </p>
          <h1 className="mt-1 text-2xl font-bold tracking-tight text-strong sm:text-3xl xl:text-4xl">
            {t("dashboard.title")}
          </h1>
          <p className="mt-1 text-sm text-muted">{t("dashboard.subtitle")}</p>
        </div>
        {showSubsetPicker && (
          <LocationSubsetPicker
            value={locationIds}
            onChange={setLocationIds}
            disabled={current.isFetching || previous.isFetching}
          />
        )}
      </header>

      {/* Filter strip - date range + previous-period toggle. Same
          surface treatment as Reports + Transactions so the three
          read as one product. */}
      <div className="flex flex-col gap-3 rounded-xl bg-surface p-4 shadow-elev-1 sm:flex-row sm:items-end sm:justify-between sm:p-5">
        <div className="flex flex-wrap items-end gap-3">
          <div className="w-44">
            <Field label={t("dashboard.fromLabel")}>
              <TextInput type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
            </Field>
          </div>
          <div className="w-44">
            <Field label={t("dashboard.toLabel")}>
              <TextInput type="date" value={to} onChange={(e) => setTo(e.target.value)} />
            </Field>
          </div>
        </div>
        <label className="inline-flex min-h-tap cursor-pointer items-center gap-2 self-start sm:self-end">
          <input
            type="checkbox"
            checked={compare}
            onChange={(e) => setCompare(e.target.checked)}
            className="h-4 w-4 rounded border-line text-brand-primary focus:ring-brand-primary"
          />
          <span className="text-sm text-foreground">{t("dashboard.compareToggle")}</span>
        </label>
      </div>

      {current.isError && (
        <Alert tone="error" title={t("dashboard.errorTitle")}>
          {current.error instanceof Error ? current.error.message : t("dashboard.errorBody")}
        </Alert>
      )}

      {current.isPending && from <= to && <PageSpinner label={t("common.loading")} />}

      {data && (
        <>
          <KpiStrip data={data} prior={compareData} />
          <PaymentMethodsRow data={data} locationId={active?.location?.id ?? null} />
          <DepartmentRow data={data} />
          <ChartsGrid data={data} locale={locale} />
          <HighValueTransactions
            data={data}
            showLocationColumn={showLocationColumn}
            locale={locale}
            locationId={active?.location?.id ?? null}
          />
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- //
// KPI strip - 6 tiles with vs.-prior delta arrows                   //
// ---------------------------------------------------------------- //

function KpiStrip(props: { readonly data: BatchOutReport; readonly prior: BatchOutReport | null }) {
  const { t } = useTranslation();
  const cur = props.data.totals;
  const prior = props.prior?.totals ?? null;

  // Order count + AOV are not in totals so we derive from payments[]
  // (charge rows only). Same for prior. Reuses the 1000-row payment
  // sample - for very high-volume scopes a future enhancement is to
  // surface order_count + aov_cents on the totals payload.
  const curOrders = countOrders(props.data.payments);
  const priorOrders = props.prior ? countOrders(props.prior.payments) : 0;

  const curAov = curOrders > 0 ? Math.round(cur.gross_cents / curOrders) : 0;
  const priorAov = prior && priorOrders > 0 ? Math.round(prior.gross_cents / priorOrders) : 0;

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-7">
      <KpiCard
        tone="emerald"
        label={t("dashboard.kpi.orders")}
        value={String(curOrders)}
        prior={prior ? priorOrders : null}
        compareValueOf={curOrders}
        formatValue={(n) => String(n)}
        icon={<ShoppingCart className="h-4 w-4" aria-hidden="true" />}
      />
      <KpiCard
        tone="violet"
        label={t("dashboard.kpi.gross")}
        value={formatCents(cur.gross_cents, "USD")}
        prior={prior ? prior.gross_cents : null}
        compareValueOf={cur.gross_cents}
        formatValue={(c) => formatCents(c, "USD")}
        icon={<TrendingUp className="h-4 w-4" aria-hidden="true" />}
      />
      <KpiCard
        tone="amber"
        label={t("dashboard.kpi.refunds")}
        value={formatCents(cur.refunded_cents, "USD")}
        prior={prior ? prior.refunded_cents : null}
        compareValueOf={cur.refunded_cents}
        formatValue={(c) => formatCents(c, "USD")}
        invertDelta
        icon={<ArrowLeftRight className="h-4 w-4" aria-hidden="true" />}
      />
      <KpiCard
        tone="amber"
        label={t("dashboard.kpi.discounts")}
        value={formatCents(cur.discount_cents ?? 0, "USD")}
        prior={prior ? (prior.discount_cents ?? 0) : null}
        compareValueOf={cur.discount_cents ?? 0}
        formatValue={(c) => formatCents(c, "USD")}
        invertDelta
        icon={<ArrowLeftRight className="h-4 w-4" aria-hidden="true" />}
      />
      <KpiCard
        tone="teal"
        label={t("dashboard.kpi.net")}
        value={formatCents(cur.deposit_cents, "USD")}
        prior={prior ? prior.deposit_cents : null}
        compareValueOf={cur.deposit_cents}
        formatValue={(c) => formatCents(c, "USD")}
        icon={<TrendingUp className="h-4 w-4" aria-hidden="true" />}
      />
      <KpiCard
        tone="blue"
        label={t("dashboard.kpi.avgOrder")}
        value={formatCents(curAov, "USD")}
        prior={prior ? priorAov : null}
        compareValueOf={curAov}
        formatValue={(c) => formatCents(c, "USD")}
        icon={<ReceiptText className="h-4 w-4" aria-hidden="true" />}
      />
      <KpiCard
        tone="rose"
        label={t("dashboard.kpi.surcharge")}
        value={formatCents(cur.surcharge_cents, "USD")}
        prior={prior ? prior.surcharge_cents : null}
        compareValueOf={cur.surcharge_cents}
        formatValue={(c) => formatCents(c, "USD")}
        icon={<TrendingUp className="h-4 w-4" aria-hidden="true" />}
      />
    </div>
  );
}

// Per-tile colour palette - gradient backdrop + glow + icon ring.
// Mirrors the Reports KPI strip's pattern (apps/web/src/pages/
// ReportsPage.tsx) so the two analytics surfaces read as the same
// visual family. Operator dogfood 2026-05-04: original tiles "looked
// boring"; punchier treatment makes the headline numbers pop.
type KpiTone = "emerald" | "violet" | "amber" | "teal" | "blue" | "rose";

const KPI_GRADIENT: Record<KpiTone, string> = {
  emerald:
    "from-emerald-100/80 via-emerald-50/60 to-card dark:from-emerald-900/40 dark:via-emerald-950/20 dark:to-card",
  violet:
    "from-violet-100/80 via-violet-50/60 to-card dark:from-violet-900/40 dark:via-violet-950/20 dark:to-card",
  amber:
    "from-amber-100/80 via-amber-50/60 to-card dark:from-amber-900/40 dark:via-amber-950/20 dark:to-card",
  teal: "from-teal-100/80 via-teal-50/60 to-card dark:from-teal-900/40 dark:via-teal-950/20 dark:to-card",
  blue: "from-blue-100/80 via-blue-50/60 to-card dark:from-blue-900/40 dark:via-blue-950/20 dark:to-card",
  rose: "from-rose-100/80 via-rose-50/60 to-card dark:from-rose-900/40 dark:via-rose-950/20 dark:to-card",
};

const KPI_GLOW: Record<KpiTone, string> = {
  emerald: "bg-emerald-400/30 dark:bg-emerald-500/20",
  violet: "bg-violet-400/30 dark:bg-violet-500/20",
  amber: "bg-amber-400/30 dark:bg-amber-500/20",
  teal: "bg-teal-400/30 dark:bg-teal-500/20",
  blue: "bg-blue-400/30 dark:bg-blue-500/20",
  rose: "bg-rose-400/30 dark:bg-rose-500/20",
};

const KPI_RING: Record<KpiTone, string> = {
  emerald: "ring-emerald-200/60 dark:ring-emerald-800/40",
  violet: "ring-violet-200/60 dark:ring-violet-800/40",
  amber: "ring-amber-200/60 dark:ring-amber-800/40",
  teal: "ring-teal-200/60 dark:ring-teal-800/40",
  blue: "ring-blue-200/60 dark:ring-blue-800/40",
  rose: "ring-rose-200/60 dark:ring-rose-800/40",
};

const KPI_ICON_RING: Record<KpiTone, string> = {
  emerald:
    "bg-emerald-100 text-emerald-700 ring-emerald-200/70 dark:bg-emerald-900/40 dark:text-emerald-300 dark:ring-emerald-800/50",
  violet:
    "bg-violet-100 text-violet-700 ring-violet-200/70 dark:bg-violet-900/40 dark:text-violet-300 dark:ring-violet-800/50",
  amber:
    "bg-amber-100 text-amber-700 ring-amber-200/70 dark:bg-amber-900/40 dark:text-amber-300 dark:ring-amber-800/50",
  teal: "bg-teal-100 text-teal-700 ring-teal-200/70 dark:bg-teal-900/40 dark:text-teal-300 dark:ring-teal-800/50",
  blue: "bg-blue-100 text-blue-700 ring-blue-200/70 dark:bg-blue-900/40 dark:text-blue-300 dark:ring-blue-800/50",
  rose: "bg-rose-100 text-rose-700 ring-rose-200/70 dark:bg-rose-900/40 dark:text-rose-300 dark:ring-rose-800/50",
};

const KPI_VALUE: Record<KpiTone, string> = {
  emerald: "text-emerald-800 dark:text-emerald-200",
  violet: "text-violet-800 dark:text-violet-200",
  amber: "text-amber-800 dark:text-amber-200",
  teal: "text-teal-800 dark:text-teal-200",
  blue: "text-blue-800 dark:text-blue-200",
  rose: "text-rose-800 dark:text-rose-200",
};

function KpiCard(props: {
  readonly tone: KpiTone;
  readonly label: string;
  readonly value: string;
  readonly prior: number | null;
  readonly compareValueOf: number;
  readonly formatValue: (n: number) => string;
  readonly icon: ReactNode;
  /** Refunds are good when they go down - flip the delta tone. */
  readonly invertDelta?: boolean;
}) {
  const { t } = useTranslation();
  const showDelta = props.prior != null;
  const delta = showDelta ? props.compareValueOf - (props.prior ?? 0) : 0;
  const pct =
    showDelta && props.prior && props.prior !== 0
      ? Math.round((delta / Math.abs(props.prior)) * 100)
      : null;
  const goingUp = delta > 0;
  const goingDown = delta < 0;
  // Tone semantics: by default up is good (emerald), down is bad
  // (amber). For "refunds" the relationship inverts.
  const positive = props.invertDelta ? goingDown : goingUp;
  const negative = props.invertDelta ? goingUp : goingDown;

  return (
    <div
      className={cn(
        "group relative overflow-hidden rounded-2xl bg-gradient-to-br p-5 shadow-elev-2 ring-1 ring-inset",
        KPI_GRADIENT[props.tone],
        KPI_RING[props.tone],
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          "pointer-events-none absolute -right-10 -top-10 h-28 w-28 rounded-full blur-2xl",
          KPI_GLOW[props.tone],
        )}
      />
      <div className="relative flex items-start justify-between gap-3">
        <span className="text-[0.6875rem] font-semibold uppercase tracking-[0.14em] text-muted">
          {props.label}
        </span>
        <span
          className={cn(
            "inline-flex h-9 w-9 items-center justify-center rounded-xl shadow-sm ring-1 ring-inset",
            KPI_ICON_RING[props.tone],
          )}
        >
          {props.icon}
        </span>
      </div>
      {/* 6-across at xl puts each tile around 200px wide; a $12,345.67
          value at text-3xl overflows the inner-overflow-hidden card and
          gets clipped. Step the size with breakpoint width so wide
          layouts get the punchy headline without losing the trailing
          cents on smaller xl screens. */}
      <p
        className={cn(
          "relative mt-3 truncate font-mono text-2xl font-bold tabular-nums tracking-tight 2xl:text-3xl",
          KPI_VALUE[props.tone],
        )}
      >
        {props.value}
      </p>
      {showDelta && (
        <div className="relative mt-1.5 flex items-center gap-1 text-xs">
          {goingUp ? (
            <ArrowUpRight
              className={cn("h-3.5 w-3.5", positive ? "text-emerald-600" : "text-amber-600")}
              aria-hidden="true"
            />
          ) : goingDown ? (
            <ArrowDownRight
              className={cn("h-3.5 w-3.5", negative ? "text-amber-600" : "text-emerald-600")}
              aria-hidden="true"
            />
          ) : (
            <Minus className="h-3.5 w-3.5 text-faint" aria-hidden="true" />
          )}
          <span
            className={cn(
              "font-mono tabular-nums",
              positive && "text-emerald-700 dark:text-emerald-400",
              negative && "text-amber-700 dark:text-amber-400",
              !positive && !negative && "text-muted",
            )}
          >
            {pct == null ? "-" : `${pct > 0 ? "+" : ""}${pct}%`}
          </span>
          <span className="text-muted">{t("dashboard.vsPrior")}</span>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- //
// Payment Methods row - 4 cards with $ + tx count + share bar       //
// ---------------------------------------------------------------- //

function PaymentMethodsRow({
  data,
  locationId,
}: {
  readonly data: BatchOutReport;
  readonly locationId: string | null;
}) {
  const { t } = useTranslation();
  const railsQuery = useQuery({
    enabled: locationId != null,
    queryKey: ["payment-rails", locationId],
    queryFn: () => getPosClient().getLocationPaymentRails(locationId!),
    staleTime: 5 * 60_000,
  });
  const totalSale = data.by_payment_type.reduce((acc, r) => acc + r.sale_cents, 0);
  const { dataMap, orderedTypes } = useMemo(() => {
    const map = new Map(data.by_payment_type.map((r) => [r.payment_type, r]));
    const seen = new Set<string>();
    const types = [
      ...[...(railsQuery.data?.rails ?? [])]
        .sort((a, b) => a.display_order - b.display_order)
        .map((r) => r.payment_type)
        .filter((pt) => {
          if (seen.has(pt)) return false;
          seen.add(pt);
          return true;
        }),
      ...data.by_payment_type.map((r) => r.payment_type).filter((pt) => !seen.has(pt)),
    ];
    return { dataMap: map, orderedTypes: types };
  }, [railsQuery.data?.rails, data.by_payment_type]);

  const rows = orderedTypes.map(
    (key) =>
      dataMap.get(key) ?? {
        payment_type: key,
        charge_count: 0,
        sale_cents: 0,
        refund_cents: 0,
        surcharge_cents: 0,
        surcharge_refund_cents: 0,
        deposit_cents: 0,
      },
  );

  return (
    <Card title={t("dashboard.paymentMethods.title")}>
      <p className="mb-4 text-xs text-muted">{t("dashboard.paymentMethods.body")}</p>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {rows.map((r) => {
          const share = totalSale > 0 ? Math.round((r.sale_cents / totalSale) * 100) : 0;
          const methodLabel = t(`dashboard.paymentMethods.${r.payment_type}`, {
            defaultValue: capitalize(r.payment_type),
          });
          return (
            <div key={r.payment_type} className="rounded-xl bg-card p-4 shadow-elev-1">
              <div className="flex items-center justify-between">
                <span className="inline-flex items-center gap-2 text-sm font-semibold text-strong">
                  <MethodIconForType
                    paymentType={r.payment_type}
                    paymentSubtype={null}
                    locationId={locationId}
                    className="h-4 w-4"
                  />
                  {methodLabel}
                </span>
                <span className="font-mono text-xs tabular-nums text-muted">{r.charge_count}</span>
              </div>
              <p className="mt-2 font-mono text-xl font-semibold tabular-nums text-strong">
                {formatCents(r.sale_cents, "USD")}
              </p>
              <ShareBar pct={share} label={`${methodLabel} ${share}%`} />
              <p className="mt-1 text-xs text-muted">
                {t("dashboard.paymentMethods.shareLabel", { pct: share })}
              </p>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

function ShareBar({ pct, label }: { readonly pct: number; readonly label: string }) {
  const clamped = Math.max(0, Math.min(100, pct));
  return (
    <div
      role="progressbar"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={clamped}
      className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-surface-muted"
    >
      <div
        style={{ width: `${clamped}%` }}
        className="h-full rounded-full bg-brand-primary transition-all"
      />
    </div>
  );
}

// ---------------------------------------------------------------- //
// Department Analysis row - N cards with $ / orders / avg / share   //
// ---------------------------------------------------------------- //

function DepartmentRow({ data }: { readonly data: BatchOutReport }) {
  const { t } = useTranslation();
  const totalSale = data.by_department.reduce((acc, r) => acc + r.sale_cents, 0);
  if (data.by_department.length === 0) {
    return null;
  }
  return (
    <Card title={t("dashboard.departments.title")}>
      <p className="mb-4 text-xs text-muted">{t("dashboard.departments.body")}</p>
      <div
        className={cn(
          "grid grid-cols-1 gap-3 sm:grid-cols-2",
          data.by_department.length >= 4 ? "lg:grid-cols-4" : "lg:grid-cols-3",
        )}
      >
        {data.by_department.map((r) => {
          const share = totalSale > 0 ? Math.round((r.sale_cents / totalSale) * 100) : 0;
          const avgCents = r.charge_count > 0 ? Math.round(r.sale_cents / r.charge_count) : 0;
          return (
            <div key={r.department_name} className="rounded-xl bg-card p-4 shadow-elev-1">
              <div className="flex items-center gap-2">
                <Building2 className="h-4 w-4 text-muted" aria-hidden="true" />
                <span className="text-sm font-semibold text-strong">{r.department_name}</span>
              </div>
              <p className="mt-2 font-mono text-xl font-semibold tabular-nums text-strong">
                {formatCents(r.sale_cents, "USD")}
              </p>
              <dl className="mt-2 space-y-0.5 text-xs">
                <div className="flex items-baseline justify-between">
                  <dt className="text-muted">{t("dashboard.departments.orders")}</dt>
                  <dd className="font-mono tabular-nums text-foreground">{r.charge_count}</dd>
                </div>
                <div className="flex items-baseline justify-between">
                  <dt className="text-muted">{t("dashboard.departments.avg")}</dt>
                  <dd className="font-mono tabular-nums text-foreground">
                    {formatCents(avgCents, "USD")}
                  </dd>
                </div>
              </dl>
              <ShareBar pct={share} label={`${r.department_name} ${share}%`} />
              <p className="mt-1 text-xs text-muted">
                {t("dashboard.paymentMethods.shareLabel", { pct: share })}
              </p>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------- //
// Charts grid - 4 line charts derived client-side from payments[]   //
// ---------------------------------------------------------------- //

interface DailyByGroup {
  date: string;
  [groupKey: string]: number | string;
}

const PAYMENT_TYPE_COLORS: Record<string, string> = {
  cash: "#10b981",
  check: "#3b82f6",
  card: "#8b5cf6",
  ach: "#f59e0b",
};

const CHART_PALETTE: readonly string[] = [
  "#8b5cf6",
  "#10b981",
  "#3b82f6",
  "#f59e0b",
  "#ef4444",
  "#14b8a6",
  "#a855f7",
  "#f97316",
];

function ChartsGrid({
  data,
  locale: _locale,
}: {
  readonly data: BatchOutReport;
  readonly locale: string;
}) {
  const { t } = useTranslation();
  // Use only charge rows for volume + revenue charts (refunds net
  // out as separate rows - the dashboard's narrative is "what came
  // in", the Reports detail table covers the full picture).
  const charges = useMemo(
    () => data.payments.filter((r) => r.link_role !== "refund"),
    [data.payments],
  );

  // Daily volume by payment type: count of charges per (date, type).
  const dailyByType = useMemo(
    () => groupDailyByKey(charges, (r) => r.payment_type ?? "unknown", false),
    [charges],
  );

  // Daily revenue by department: sum approved_cents / 100 → dollars per (date, dept).
  const dailyByDept = useMemo(
    () => groupDailyByKey(charges, (r) => r.department_name, true),
    [charges],
  );

  // Daily card volume by subtype - narrowed to payment_type === card.
  const dailyByCardSubtype = useMemo(
    () =>
      groupDailyByKey(
        charges.filter((r) => r.payment_type === "card"),
        (r) => r.payment_subtype ?? "unknown",
        false,
      ),
    [charges],
  );

  // Daily volume by device - narrowed to rows that carry a device id.
  const dailyByDevice = useMemo(
    () =>
      groupDailyByKey(
        charges.filter((r) => !!r.device_id),
        (r) => r.device_id ?? "unknown",
        false,
      ),
    [charges],
  );

  return (
    <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
      <ChartCard
        title={t("dashboard.charts.volumeByType")}
        body={t("dashboard.charts.volumeByTypeBody")}
        rows={dailyByType.rows}
        keys={dailyByType.keys}
        valueFormatter={countFormatter}
        colorFor={(k) => PAYMENT_TYPE_COLORS[k] ?? CHART_PALETTE[0]}
      />
      <ChartCard
        title={t("dashboard.charts.revenueByDept")}
        body={t("dashboard.charts.revenueByDeptBody")}
        rows={dailyByDept.rows}
        keys={dailyByDept.keys}
        valueFormatter={dollarFormatter}
      />
      <ChartCard
        title={t("dashboard.charts.cardBySubtype")}
        body={t("dashboard.charts.cardBySubtypeBody")}
        rows={dailyByCardSubtype.rows}
        keys={dailyByCardSubtype.keys}
        valueFormatter={countFormatter}
      />
      <ChartCard
        title={t("dashboard.charts.byDevice")}
        body={t("dashboard.charts.byDeviceBody")}
        rows={dailyByDevice.rows}
        keys={dailyByDevice.keys}
        valueFormatter={countFormatter}
        emptyHint={t("dashboard.charts.byDeviceEmpty")}
      />
    </div>
  );
}

function ChartCard(props: {
  readonly title: string;
  readonly body: string;
  readonly rows: ReadonlyArray<DailyByGroup>;
  readonly keys: ReadonlyArray<string>;
  readonly valueFormatter: (v: number) => string;
  /** Optional per-key colour. When omitted we cycle the palette by
   *  the key's index in ``props.keys``. */
  readonly colorFor?: (key: string) => string;
  readonly emptyHint?: string;
}) {
  const { t } = useTranslation();
  if (props.rows.length === 0 || props.keys.length === 0) {
    return (
      <Card title={props.title}>
        <p className="text-xs text-muted">{props.body}</p>
        <p className="mt-6 text-sm text-muted">
          {props.emptyHint ?? t("dashboard.charts.emptyDefault")}
        </p>
      </Card>
    );
  }
  const colourForIndex = (key: string, idx: number): string =>
    props.colorFor ? props.colorFor(key) : (CHART_PALETTE[idx % CHART_PALETTE.length] ?? "#8b5cf6");
  return (
    <Card title={props.title}>
      <p className="mb-3 text-xs text-muted">{props.body}</p>
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={props.rows as DailyByGroup[]}>
            {/* Recharts ignores Tailwind tokens - feed it the raw HSL
                triplet from each CSS variable. The earlier rev used
                ``rgb(var(--muted))`` etc., but our tokens are HSL
                triplets ("25 5% 70%") and the actual variable names
                are ``--text-muted`` / ``--border`` / ``--surface``,
                not bare ``--muted`` / ``--line`` / ``--card``. The
                broken refs fell back to recharts' defaults - barely-
                visible greys on dark mode (2026-05-03 dogfood). */}
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border) / 0.5)" />
            <XAxis
              dataKey="date"
              tick={{ fill: "hsl(var(--text-muted))", fontSize: 11 }}
              stroke="hsl(var(--border))"
            />
            <YAxis
              tick={{ fill: "hsl(var(--text-muted))", fontSize: 11 }}
              stroke="hsl(var(--border))"
              tickFormatter={props.valueFormatter}
            />
            <Tooltip
              contentStyle={{
                background: "hsl(var(--surface))",
                border: "1px solid hsl(var(--border))",
                borderRadius: 8,
                fontSize: 12,
                color: "hsl(var(--text-strong))",
              }}
              labelStyle={{ color: "hsl(var(--text-strong))" }}
              itemStyle={{ color: "hsl(var(--text))" }}
              formatter={(value) => props.valueFormatter(Number(value))}
            />
            <Legend wrapperStyle={{ fontSize: 11, color: "hsl(var(--text-muted))" }} />
            {props.keys.map((k, i) => (
              <Line
                key={k}
                type="monotone"
                dataKey={k}
                stroke={colourForIndex(k, i)}
                strokeWidth={2}
                dot={false}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------- //
// Recent High-Value Transactions table - top N by approved_cents    //
// ---------------------------------------------------------------- //

function HighValueTransactions(props: {
  readonly data: BatchOutReport;
  readonly showLocationColumn: boolean;
  readonly locale: string;
  readonly locationId: string | null;
}) {
  const { t } = useTranslation();
  const rows = useMemo(() => {
    const charges = props.data.payments.filter((r) => r.link_role !== "refund");
    return [...charges]
      .sort((a, b) => b.approved_cents - a.approved_cents)
      .slice(0, HIGH_VALUE_TOP_N);
  }, [props.data.payments]);

  if (rows.length === 0) {
    return null;
  }

  return (
    <Card title={t("dashboard.highValue.title")}>
      <p className="mb-3 text-xs text-muted">{t("dashboard.highValue.body")}</p>
      <div className="-mx-5 -mb-5 overflow-x-auto sm:-mx-6 sm:-mb-6">
        <table className="w-full text-sm">
          <thead className="bg-surface-muted text-left text-[0.6875rem] font-semibold uppercase tracking-wider text-muted">
            <tr>
              <th scope="col" className="px-5 py-3 sm:px-6">
                {t("dashboard.highValue.colReference")}
              </th>
              <th scope="col" className="px-3 py-3">
                {t("dashboard.highValue.colCustomer")}
              </th>
              {props.showLocationColumn && (
                <th scope="col" className="px-3 py-3">
                  {t("dashboard.highValue.colLocation")}
                </th>
              )}
              <th scope="col" className="px-3 py-3">
                {t("dashboard.highValue.colDepartment")}
              </th>
              <th scope="col" className="px-3 py-3 text-right">
                {t("dashboard.highValue.colAmount")}
              </th>
              <th scope="col" className="px-3 py-3 text-right">
                {t("dashboard.highValue.colSurcharge")}
              </th>
              <th scope="col" className="px-3 py-3">
                {t("dashboard.highValue.colMethod")}
              </th>
              <th scope="col" className="px-5 py-3 sm:px-6">
                {t("dashboard.highValue.colDate")}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line/60">
            {rows.map((r, i) => (
              <HighValueRow
                key={r.payment_id}
                row={r}
                zebra={i % 2 === 1}
                showLocationColumn={props.showLocationColumn}
                locationId={props.locationId}
              />
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function HighValueRow(props: {
  readonly row: BatchOutPaymentRow;
  readonly zebra: boolean;
  readonly showLocationColumn: boolean;
  readonly locationId: string | null;
}) {
  const r = props.row;
  return (
    <tr className={cn(props.zebra && "bg-surface-muted/40")}>
      <td className="whitespace-nowrap px-5 py-3 font-mono text-xs text-strong sm:px-6">
        {r.order_id ? (
          <Link to={`/transactions/${r.order_id}`} className="hover:underline">
            {r.order_reference ?? r.external_ref ?? "-"}
          </Link>
        ) : (
          (r.order_reference ?? r.external_ref ?? "-")
        )}
      </td>
      <td className="px-3 py-3 text-foreground">
        {r.customer_name ?? <span className="text-faint">-</span>}
      </td>
      {props.showLocationColumn && (
        <td className="px-3 py-3 text-foreground">
          {r.location_name ?? <span className="text-faint">-</span>}
        </td>
      )}
      <td className="px-3 py-3 text-foreground">{r.department_name}</td>
      <td className="whitespace-nowrap px-3 py-3 text-right font-mono font-semibold tabular-nums text-strong">
        {formatCents(r.approved_cents, "USD")}
      </td>
      <td className="whitespace-nowrap px-3 py-3 text-right font-mono tabular-nums text-foreground">
        {r.surcharge_cents > 0 ? formatCents(r.surcharge_cents, "USD") : "-"}
      </td>
      <td className="px-3 py-3 capitalize text-foreground">
        <span className="inline-flex items-center gap-1.5">
          <MethodIconForType
            paymentType={r.payment_type ?? ""}
            paymentSubtype={null}
            locationId={props.locationId}
            className="h-4 w-4"
          />
          {r.payment_type ?? "-"}
        </span>
      </td>
      <td className="whitespace-nowrap px-5 py-3 font-mono text-xs tabular-nums text-foreground sm:px-6">
        {r.created_at.slice(0, 10)}
      </td>
    </tr>
  );
}

// ---------------------------------------------------------------- //
// Helpers                                                           //
// ---------------------------------------------------------------- //

function countOrders(payments: ReadonlyArray<BatchOutPaymentRow>): number {
  // One order can have multiple payment rows (split tender). Count
  // distinct order_ids on charge rows so AOV math reflects reality.
  const ids = new Set<string>();
  for (const r of payments) {
    if (r.link_role === "refund") continue;
    if (r.order_id) ids.add(r.order_id);
  }
  return ids.size;
}

function capitalize(s: string): string {
  return s.length > 0 ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

/** Fold ``payments[]`` into one row per date with one column per
 *  group key. Counts when ``asAmount`` is false; sums dollars when
 *  true. Result is ready to feed into recharts ``LineChart``. */
function groupDailyByKey(
  rows: ReadonlyArray<BatchOutPaymentRow>,
  keyFn: (r: BatchOutPaymentRow) => string,
  asAmount: boolean,
): { readonly rows: ReadonlyArray<DailyByGroup>; readonly keys: ReadonlyArray<string> } {
  // Build a (date → key → number) map, then pivot to one record per
  // date. Date pulled from the first 10 chars of created_at - both
  // V1 and V2 emit ISO-8601 so the substring is location-local-day
  // safe.
  const byDate = new Map<string, Map<string, number>>();
  const allKeys = new Set<string>();
  for (const r of rows) {
    const date = r.created_at.slice(0, 10);
    const key = keyFn(r) || "unknown";
    allKeys.add(key);
    const bucket = byDate.get(date) ?? new Map<string, number>();
    const inc = asAmount ? r.approved_cents / 100 : 1;
    bucket.set(key, (bucket.get(key) ?? 0) + inc);
    byDate.set(date, bucket);
  }
  const dates = Array.from(byDate.keys()).sort();
  const keys = Array.from(allKeys).sort();
  const out: DailyByGroup[] = dates.map((date) => {
    const bucket = byDate.get(date)!;
    const row: DailyByGroup = { date };
    for (const k of keys) {
      row[k] = round2(bucket.get(k) ?? 0);
    }
    return row;
  });
  return { rows: out, keys };
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

function countFormatter(v: number): string {
  return String(v);
}

function dollarFormatter(v: number): string {
  if (v >= 1000) return `$${Math.round(v / 100) / 10}k`;
  return `$${v.toFixed(0)}`;
}

/** ISO date for "today" in UTC. */
function todayUtcIso(): string {
  const now = new Date();
  return [
    now.getUTCFullYear(),
    String(now.getUTCMonth() + 1).padStart(2, "0"),
    String(now.getUTCDate()).padStart(2, "0"),
  ].join("-");
}

function nDaysAgoUtcIso(n: number): string {
  const now = new Date();
  now.setUTCDate(now.getUTCDate() - n);
  return [
    now.getUTCFullYear(),
    String(now.getUTCMonth() + 1).padStart(2, "0"),
    String(now.getUTCDate()).padStart(2, "0"),
  ].join("-");
}

/** Given the current window [from, to], compute the prior-period
 *  window of the same length immediately preceding it. Both bounds
 *  inclusive - a 7-day window [Mar 24, Mar 30] yields prior [Mar 17,
 *  Mar 23]. */
function deriveOffsetWindow(
  from: string,
  to: string,
): { readonly from: string; readonly to: string } {
  const fromDate = new Date(`${from}T00:00:00Z`);
  const toDate = new Date(`${to}T00:00:00Z`);
  const lengthMs = toDate.getTime() - fromDate.getTime();
  const priorTo = new Date(fromDate.getTime() - 24 * 60 * 60 * 1000);
  const priorFrom = new Date(priorTo.getTime() - lengthMs);
  return { from: isoDate(priorFrom), to: isoDate(priorTo) };
}

function isoDate(d: Date): string {
  return [
    d.getUTCFullYear(),
    String(d.getUTCMonth() + 1).padStart(2, "0"),
    String(d.getUTCDate()).padStart(2, "0"),
  ].join("-");
}
